import { useState, useCallback, useRef, useEffect } from 'react';

import { cartFunctions, isValidRawCart, updateCartCountInHeader } from '../utils/promo-cart-utils';

const { fetchCartHelper, updateCartHelper, addToCartHelper } = cartFunctions;

export const useCart = () => {
  const isMounted = useRef(false);
  const [currentCartModel, setCurrentCartModel] = useState();
  const [frozenCartModel, setFrozenCartModel] = useState();
  // If a user adds/updates items in cart before initial cart call comes back
  const initialFetchComplete = useRef(false);
  const [isCartLoading, setIsCartLoading] = useState(true);

  const [thdCartReady] = useState(() => new Promise((resolve) => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('global.thd-cart-loaded', () => {
      resolve();
    });
  }));

  const handleReturnedCartModel = (newCartModel) => {
    if (isMounted.current) {
      initialFetchComplete.current = true;
      setCurrentCartModel(newCartModel);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    // listen to generic add to cart events and handle the results
    window.LIFE_CYCLE_EVENT_BUS.on('add-to-cart.success', (cartSuccessEvent) => {
      if (cartSuccessEvent?.eventName === 'success' && isValidRawCart(cartSuccessEvent.output?.CartModel)) {
        handleReturnedCartModel(cartSuccessEvent.output.CartModel);
      }
    });
  }, []);

  useEffect(() => {
    thdCartReady.then(() => {
      if (!initialFetchComplete.current && isMounted.current) {
        setIsCartLoading(true);
        fetchCartHelper()
          .then((newCartModel) => {
            // Doesn't allow cart to be overwritten if we have already received data.
            if (!initialFetchComplete.current) {
              handleReturnedCartModel(newCartModel);
            }
          })
          .catch(() => {
            if (isMounted.current) setIsCartLoading(false);
          });
      }
    });
  }, [thdCartReady]);

  // should always have currentCartModel in dep array
  useEffect(() => {
    if (isMounted.current && !frozenCartModel) setIsCartLoading(false);
  }, [currentCartModel, frozenCartModel]);

  const fetchCart = useCallback(() => {
    if (isMounted.current) {
      setIsCartLoading(true);

      return thdCartReady
        .then(fetchCartHelper)
        .then((newCartModel) => {
          handleReturnedCartModel(newCartModel);
          return newCartModel;
        })
        .catch((error) => {
          setIsCartLoading(false);
          return Promise.reject(error);
        });
    }

    return Promise.reject(new Error('useCart is unmounted'));
  }, [thdCartReady]);

  const addToCart = useCallback((CartRequest) => {
    if (isMounted.current) {
      setIsCartLoading(true);

      return thdCartReady
        .then(() => addToCartHelper(CartRequest))
        .then((newCartModel) => {
          handleReturnedCartModel(newCartModel);

          updateCartCountInHeader(newCartModel);

          window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-cart.success', { CartModel: newCartModel });

          return newCartModel;
        })
        .catch(fetchCart);
    }

    return Promise.reject(new Error('useCart is unmounted'));
  }, [thdCartReady, fetchCart]);

  const updateCart = useCallback((CartRequest) => {
    if (isMounted.current) {
      setIsCartLoading(true);

      return thdCartReady
        .then(() => updateCartHelper(CartRequest))
        .then((newCartModel) => {
          handleReturnedCartModel(newCartModel);

          updateCartCountInHeader(newCartModel);

          window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-cart.success', { CartModel: newCartModel });

          return newCartModel;
        })
        .catch(fetchCart);
    }

    return Promise.reject(new Error('useCart is unmounted'));
  }, [thdCartReady, fetchCart]);

  // when consumer calls freezeCart it takes the current value of cart and stores it in state,
  // it also returns a function that will "unfreeze" the cart by resetting it to null
  const freezeCart = useCallback(() => {
    if (isMounted.current) setFrozenCartModel(currentCartModel);

    return () => {
      if (isMounted.current) setFrozenCartModel(null);
    };
  }, [currentCartModel]);

  return {
    // if cart is "frozen" return the frozen value of cart, otherwise return the latest
    CartModel: frozenCartModel || currentCartModel,
    addToCart,
    updateCart,
    isCartLoading,
    setIsCartLoading,
    freezeCart
  };
};
